/**
 * Synthesis panel — per-good toggle, priority, and TARGET controls over
 * FactoryState.synthesis (spec §4.4, §11 setSynthesis command).
 *
 * DOM is built once; `update` only reflects state onto existing nodes. Click
 * handlers issue the INVERSE/adjacent value of the last value seen from
 * state, cached per-row in the `known` closure below — panels never hold a
 * GameState to read back from, so this cache is the only way a handler can
 * know what "currently enabled" or "currently priority" means.
 *
 * The target box is the one control here that is not a fixed step: type a
 * number and the factory keeps that component topped up to it and then stops.
 * Blank means no limit, which is where every recipe starts.
 */

import { el, section, setText } from './panel';
import type { Panel, Issue } from './panel';
import type { GameState } from '../../sim/types';
import { GOOD_IDS, GOODS, type GoodId } from '../../sim/data';

/** Spec §4.4: fertilizer (14e) is deliberately the most expensive synthesis. */
function highestEnergyGood(): GoodId {
  let best: GoodId | undefined;
  let bestEnergy = -Infinity;
  for (const id of GOOD_IDS) {
    const energy = GOODS[id].energy;
    if (energy > bestEnergy) {
      bestEnergy = energy;
      best = id;
    }
  }
  // GOOD_IDS is a non-empty compile-time constant, so a max is always found.
  return best as GoodId;
}

interface Known {
  enabled: boolean;
  priority: number;
  target: number;
}

export function createSynthesisPanel(issue: Issue): Panel {
  const { root, body } = section('Synthesis');
  const warnGood = highestEnergyGood();

  const toggles = {} as Record<GoodId, HTMLButtonElement>;
  const prioSpans = {} as Record<GoodId, HTMLElement>;
  const targetInputs = {} as Record<GoodId, HTMLInputElement>;
  const stockSpans = {} as Record<GoodId, HTMLElement>;
  const known = {} as Record<GoodId, Known>;

  for (const id of GOOD_IDS) {
    known[id] = { enabled: false, priority: 0, target: 0 };

    const row = el('div', 'row');

    const toggle = el('button', 'toggle', GOODS[id].label);
    toggle.addEventListener('click', () => {
      const current = known[id];
      issue({
        type: 'setSynthesis',
        good: id,
        enabled: !current.enabled,
        priority: current.priority,
        target: current.target,
      });
    });

    // Energy PER UNIT PRODUCED. It was labelled "8e" and "14e", which named a
    // unit nobody has heard of; the tooltip now says what the number is.
    const energyValue = el('span', 'row-value', `${GOODS[id].energy}/u`);
    energyValue.title = `${GOODS[id].energy} energy per unit produced`;
    if (id === warnGood) energyValue.classList.add('warn');

    const prioSpan = el('span', 'prio', 'p0');

    const prioUp = el('button', 'prio', '▲');
    prioUp.addEventListener('click', () => {
      const current = known[id];
      issue({
        type: 'setSynthesis',
        good: id,
        enabled: current.enabled,
        priority: Math.max(0, current.priority - 1),
        target: current.target,
      });
    });

    const prioDown = el('button', 'prio', '▼');
    prioDown.addEventListener('click', () => {
      const current = known[id];
      issue({
        type: 'setSynthesis',
        good: id,
        enabled: current.enabled,
        priority: current.priority + 1,
        target: current.target,
      });
    });

    row.append(toggle, energyValue, prioSpan, prioUp, prioDown);

    // Second line: how many are held, and how many to stop at.
    const targetRow = el('div', 'row row-sub');

    const stock = el('span', 'stock', '0 held');
    stock.title = 'how many the colony holds now';

    const label = el('span', 'target-label', 'keep');

    const input = el('input', 'target') as HTMLInputElement;
    input.type = 'number';
    input.min = '0';
    input.step = '10';
    input.placeholder = '∞';
    input.title = 'Stop synthesising at this many. Blank for no limit.';

    // On change, not input: committing on every keystroke would issue a command
    // for "4" on the way to typing "400", and the factory would act on it.
    input.addEventListener('change', () => {
      const raw = input.value.trim();
      const parsed = raw === '' ? 0 : Number.parseInt(raw, 10);
      const target = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
      const current = known[id];
      issue({
        type: 'setSynthesis',
        good: id,
        enabled: current.enabled,
        priority: current.priority,
        target,
      });
    });

    targetRow.append(stock, label, input);
    body.append(row, targetRow);

    toggles[id] = toggle;
    prioSpans[id] = prioSpan;
    targetInputs[id] = input;
    stockSpans[id] = stock;
  }

  function update(state: GameState): void {
    for (const id of GOOD_IDS) {
      const setting = state.factory.synthesis[id];
      known[id] = {
        enabled: setting.enabled,
        priority: setting.priority,
        target: setting.target,
      };
      toggles[id].classList.toggle('toggle-on', setting.enabled);
      setText(prioSpans[id], `p${setting.priority}`);

      const stockValue = state.inventory.goods[id];
      setText(stockSpans[id], `${stockValue} held`);

      // Never write over a box being typed into. The panel updates every frame,
      // so without this a half-typed number would be replaced mid-keystroke.
      const input = targetInputs[id];
      if (document.activeElement !== input) {
        input.value = setting.target > 0 ? String(setting.target) : '';
      }

      // Dim the row once the target is met, so a factory sitting idle on
      // purpose looks different from one that is stuck.
      const met = setting.target > 0 && stockValue >= setting.target;
      stockSpans[id].classList.toggle('at-target', met);
    }
  }

  return { root, update };
}
